<template>
    <main-layout>
        <div class="page-head" slot="header">
            <div>
                <div class="page-kicker">Accounting</div>
                <h2>Bills to publish</h2>
            </div>
            <div class="page-actions">
                <router-link class="el-button el-button--default el-button--small" to="/buildium/dext-bills-download">Import from Dext</router-link>
                <el-button size="small" icon="el-icon-refresh" @click="syncFromBuildium">Sync from Buildium</el-button>
            </div>
        </div>

        <div :class="{'has-panel': !!detail}">
            <div class="list-filters">
                <el-input v-model="params.q" class="list-search" clearable prefix-icon="el-icon-search"
                          placeholder="Vendor, property, memo or reference" aria-label="Search bills"/>
                <button v-for="c in statusChips" :key="c.value" type="button" class="chip" :class="{on: params.uploadstatus === c.value}"
                        :aria-pressed="String(params.uploadstatus === c.value)" @click="toggleStatus(c.value)">{{ c.label }}
                </button>
                <button type="button" class="chip chip-more" :aria-expanded="String(moreFilters)" @click="moreFilters = !moreFilters">
                    {{ moreFilters ? 'Fewer filters' : '+ Paid, dates' }}
                </button>
                <span class="spacer"></span>
                <el-dropdown trigger="click" @command="onListMore">
                    <el-button size="small">All bills <i class="el-icon-arrow-down el-icon--right"></i></el-button>
                    <el-dropdown-menu slot="dropdown">
                        <el-dropdown-item command="publishall">Publish all unpublished…</el-dropdown-item>
                        <el-dropdown-item command="uploadall">Attach all missing files…</el-dropdown-item>
                        <el-dropdown-item command="deleteall" divided class="danger-item">Delete all unpublished…</el-dropdown-item>
                    </el-dropdown-menu>
                </el-dropdown>
            </div>
            <div v-if="moreFilters" class="list-filters">
                <el-select size="small" v-model="params.paidstatus" placeholder="Paid or unpaid" clearable @change="onSearch">
                    <el-option label="Paid" value="Paid"/>
                    <el-option label="Unpaid" value="Unpaid"/>
                </el-select>
                <el-date-picker v-model="params.daterange" type="daterange" size="small" range-separator="–"
                                start-placeholder="Bill date from" end-placeholder="to" value-format="yyyy-MM-dd" @change="onSearch"/>
                <el-date-picker v-model="params.uploaddaterange" type="daterange" size="small" range-separator="–"
                                start-placeholder="Published from" end-placeholder="to" value-format="yyyy-MM-dd" @change="onSearch"/>
                <el-button size="small" type="text" @click="resetFilters">Clear all filters</el-button>
            </div>

            <div v-if="showPublishProgress" class="list-card bills-progress">
                <div class="bills-progress-head">
                    <b>{{ isFileUpload ? 'Attaching files' : 'Publishing to Buildium' }}</b>
                    <span>{{ Math.min(publishProgress, 100) }}%</span>
                    <span class="spacer"></span>
                    <span v-if="publishResults" class="pill pill-ok">{{ publishResults.total_success }} done</span>
                    <span v-if="publishResults && publishResults.total_failed" class="pill pill-bad">{{ publishResults.total_failed }} failed</span>
                    <el-button v-if="publishProgress >= 100" size="mini" type="text" @click="showPublishProgress = false">Hide</el-button>
                </div>
                <el-progress :percentage="Math.min(publishProgress, 100)" :stroke-width="10" :show-text="false"/>
            </div>

            <section class="list-card" v-loading="loading">
                <el-table ref="table" :data="dataList" class="list-table" @selection-change="onSelectionChange"
                          @row-click="onRowClick" :row-class-name="rowClass">
                    <el-table-column width="44" type="selection"/>
                    <el-table-column label="Pay to" min-width="200">
                        <template slot-scope="{row}">
                            <div class="cell-title">{{ row.VendorName || '—' }}</div>
                            <div class="cell-sub">{{ [row.Memo, row.ReferenceNumber].filter(Boolean).join(' · ') }}</div>
                        </template>
                    </el-table-column>
                    <el-table-column label="Property or company" min-width="200">
                        <template slot-scope="{row}">
                            <div>{{ row.PropertyOrCompany || '—' }}</div>
                            <div class="cell-sub">{{ row.AccountName }}</div>
                        </template>
                    </el-table-column>
                    <el-table-column label="Amount" width="130" align="right">
                        <template slot-scope="{row}">
                            <span class="cell-num" :class="{'amount-negative': parseFloat(row.Amount) < 0}">{{ money(row.Amount) }}</span>
                            <div v-if="Number(row.GST)" class="cell-sub">GST {{ money(row.GST) }}</div>
                        </template>
                    </el-table-column>
                    <el-table-column label="Date" width="120">
                        <template slot-scope="{row}">
                            <div>{{ date(row.Date) }}</div>
                            <div v-if="row.DueDate" class="cell-sub">Due {{ date(row.DueDate) }}</div>
                        </template>
                    </el-table-column>
                    <el-table-column label="Status" width="190">
                        <template slot-scope="{row}">
                            <span class="pill" :class="'pill-' + uploadStatus(row).kind">{{ uploadStatus(row).text }}</span>
                            <span v-if="row.Status === 'Paid'" class="pill pill-muted">Paid</span>
                        </template>
                    </el-table-column>
                </el-table>
                <div class="list-footer">
                    <span class="text-muted">{{ total }} bill{{ total === 1 ? '' : 's' }}</span>
                    <el-pagination background layout="sizes, prev, pager, next" :total="total" :page-size="pageSize"
                                   :page-sizes="[20, 50, 100]" :current-page="page"
                                   @current-change="onPageChange" @size-change="onSizeChange"/>
                </div>
            </section>

            <bulk-bar :count="selectionIds.length" @clear="$refs.table.clearSelection()">
                <el-button size="small" :disabled="!selectedUnpublished.length" @click="publishSelected">
                    Publish {{ selectedUnpublished.length || '' }}
                </el-button>
                <el-button size="small" :disabled="!selectedMissingFiles.length" @click="uploadSelected">
                    Attach files {{ selectedMissingFiles.length || '' }}
                </el-button>
                <el-dropdown trigger="click" @command="cmd => cmd === 'delete' && deleteBills(selectionIds)">
                    <el-button size="small">More <i class="el-icon-arrow-down el-icon--right"></i></el-button>
                    <el-dropdown-menu slot="dropdown">
                        <el-dropdown-item command="delete" class="danger-item">Delete…</el-dropdown-item>
                    </el-dropdown-menu>
                </el-dropdown>
            </bulk-bar>
        </div>

        <side-panel v-if="detail" open kicker="Bill" :title="detail.VendorName || 'Bill'" :subtitle="detail.PropertyOrCompany"
                    :record-key="detail.Billsid" @close="detail = null">
            <div>
                <span class="pill" :class="'pill-' + uploadStatus(detail).kind">{{ uploadStatus(detail).text }}</span>
                <span class="pill pill-muted">{{ detail.Status || 'Unpaid' }}</span>
            </div>
            <div class="sp-facts">
                <div><span>Amount</span><b>{{ money(detail.Amount) }}</b></div>
                <div><span>GST</span><b>{{ money(detail.GST) || '—' }}</b></div>
                <div><span>Bill date</span><b>{{ date(detail.Date) || '—' }}</b></div>
                <div><span>Due</span><b>{{ date(detail.DueDate) || '—' }}</b></div>
                <div><span>Account</span><b>{{ detail.AccountName || '—' }}</b></div>
                <div><span>GST account</span><b>{{ detail.GSTAccountName || '—' }}</b></div>
                <div><span>Memo</span><b>{{ detail.Memo || '—' }}</b></div>
                <div><span>Reference</span><b>{{ detail.ReferenceNumber || '—' }}</b></div>
            </div>
            <div v-if="detail.Description">
                <h3>Description</h3>
                <p class="bills-desc">{{ detail.Description }}</p>
            </div>
            <div class="sp-actions">
                <el-button v-if="Number(detail.UploadStatus) === 0" type="primary" @click="publish([detail])">Publish to Buildium</el-button>
                <el-button v-if="Number(detail.UploadStatus) === 1" type="primary" @click="upload([detail])">Attach files</el-button>
                <el-button @click="deleteBills([detail])">Delete…</el-button>
            </div>
            <p v-if="detail.UploadDate" class="text-muted">Published {{ date(detail.UploadDate) }}</p>
        </side-panel>
    </main-layout>
</template>

<script>
import Pagination from "../mixins/Pagination";
import ApiService from "../utils/ApiService";
import SidePanel from "../components/SidePanel.vue";
import BulkBar from "../components/BulkBar.vue";
import {formatMoney, formatDate} from "../sterling/formHelpers";

const UPLOAD = {
    0: {text: 'Not published', kind: 'warn'},
    1: {text: 'Files not attached', kind: 'warn'},
    2: {text: 'Not synchronized', kind: 'info'},
    3: {text: 'Synchronized', kind: 'ok'},
};

function emptyParams() {
    return {
        q: '', memo: '', vendor: '', referencenumber: '', property_name: '', association_name: '',
        paidstatus: '', uploadstatus: '', daterange: [], uploaddaterange: [],
    };
}

export default {
    name: "BuildiumBills",
    components: {SidePanel, BulkBar},
    mixins: [Pagination],
    data() {
        return {
            params: emptyParams(),
            pageSize: 50,
            moreFilters: false,
            detail: null,
            searchTimer: null,
            statusChips: [
                {label: 'Not published', value: '0'},
                {label: 'Files not attached', value: '1'},
                {label: 'Not synchronized', value: '2'},
                {label: 'Synchronized', value: '3'},
            ],
            showPublishProgress: false,
            publishProgress: 0,
            publishPollingInterval: null,
            publishResults: null,
            isFileUpload: false,
        }
    },
    computed: {
        selectedUnpublished() {
            return this.selectionIds.filter(b => Number(b.UploadStatus) === 0);
        },
        selectedMissingFiles() {
            return this.selectionIds.filter(b => Number(b.UploadStatus) === 1);
        },
    },
    watch: {
        'params.q'() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(this.onSearch, 350);
        },
    },
    methods: {
        listApi() {
            return '/buildium/all-bills';
        },
        listParams() {
            return this.params;
        },
        money: formatMoney,
        date: formatDate,
        uploadStatus(row) {
            return UPLOAD[Number(row.UploadStatus)] || {text: 'Unknown', kind: 'muted'};
        },
        toggleStatus(value) {
            this.params.uploadstatus = this.params.uploadstatus === value ? '' : value;
            this.onSearch();
        },
        resetFilters() {
            this.params = emptyParams();
            this.onSearch();
        },
        onSizeChange(size) {
            this.pageSize = size;
            this.onSearch();
        },
        onRowClick(row, column) {
            if (column && column.type === 'selection') return;
            this.detail = row;
        },
        rowClass({row}) {
            return this.detail && this.detail.Billsid === row.Billsid ? 'is-open' : '';
        },
        onFinish() {
            if (this.detail) {
                this.detail = this.dataList.find(b => b.Billsid === this.detail.Billsid) || null;
            }
        },
        // How many bills a "do it to all" action would touch; asked before confirming.
        countWhere(uploadstatus) {
            return ApiService.get('/buildium/all-bills', {params: {uploadstatus, limit: 1}}).then(r => r.data.total);
        },
        onListMore(cmd) {
            const status = {publishall: '0', uploadall: '1', deleteall: '0'}[cmd];
            this.countWhere(status).then(n => {
                if (!n) {
                    this.$message.info(cmd === 'uploadall' ? 'No bills are missing files' : 'No unpublished bills');
                    return;
                }
                if (cmd === 'publishall') this.publish([], n);
                if (cmd === 'uploadall') this.upload([], n);
                if (cmd === 'deleteall') this.deleteAllUnpublished(n);
            }).catch(reason => {
                this.$message.error(reason.message);
            });
        },
        publishSelected() {
            this.publish(this.selectedUnpublished);
        },
        uploadSelected() {
            this.upload(this.selectedMissingFiles);
        },
        // bills: the ones to publish, or [] with count = all unpublished bills.
        publish(bills, count) {
            const ids = bills.filter(b => Number(b.UploadStatus) === 0).map(b => b.Billsid);
            if (bills.length && !ids.length) {
                this.$message.info('These bills are already in Buildium');
                return;
            }
            const n = ids.length || count;
            this.$confirm(`Create ${n} bill${n === 1 ? '' : 's'} in Buildium?`, 'Publish', {confirmButtonText: `Publish ${n}`}).then(() => {
                this.loading = true;
                this.showPublishProgress = true;
                this.isFileUpload = false;
                ApiService.post('/buildium/all-bills/publish-bills', ids.length ? {Billsid: ids} : {}).then(() => {
                    this.startPolling('/buildium/all-bills/publish-status', 'Publish');
                }).catch(reason => {
                    this.showPublishProgress = false;
                    this.$message.error(reason.message);
                }).finally(() => {
                    this.loading = false;
                });
            }).catch(() => {
            });
        },
        upload(bills, count) {
            const ids = bills.filter(b => Number(b.UploadStatus) === 1).map(b => b.Billsid);
            const n = ids.length || count;
            this.$confirm(`Attach the files of ${n} bill${n === 1 ? '' : 's'} in Buildium?`, 'Attach files', {confirmButtonText: 'Attach'}).then(() => {
                this.loading = true;
                this.showPublishProgress = true;
                this.isFileUpload = true;
                ApiService.post('/buildium/all-bills/upload-files', {Billsid: ids}).then(() => {
                    this.startPolling('/buildium/all-bills/upload-status', 'File upload');
                }).catch(reason => {
                    this.showPublishProgress = false;
                    this.$message.error(reason.message);
                }).finally(() => {
                    this.loading = false;
                });
            }).catch(() => {
            });
        },
        deleteBills(bills) {
            const ids = bills.map(b => b.Billsid);
            const inBuildium = bills.filter(b => Number(b.UploadStatus) !== 0).length;
            const note = inBuildium ? ` ${inBuildium} of them are already in Buildium; they stay there and are only removed from this list.` : '';
            this.$confirm(`Delete ${ids.length} bill${ids.length === 1 ? '' : 's'} from the Portal?${note}`, 'Delete bills',
                {type: 'warning', confirmButtonText: `Delete ${ids.length}`, confirmButtonClass: 'el-button--danger'}).then(() => {
                this.loading = true;
                ApiService.delete('/buildium/all-bills/batch', {data: {Billsid: ids}}).then(() => {
                    if (this.detail && ids.includes(this.detail.Billsid)) this.detail = null;
                    this.$refs.table && this.$refs.table.clearSelection();
                    this.$message.success(`${ids.length} bill(s) deleted`);
                    this.fetchList();
                }).catch(reason => {
                    this.$message.error(reason.message);
                }).finally(() => {
                    this.loading = false;
                });
            }).catch(() => {
            });
        },
        deleteAllUnpublished(n) {
            this.$confirm(`Delete all ${n} unpublished bills from the Portal? They were imported from Dext and have not been sent to Buildium; this cannot be undone.`,
                'Delete all unpublished', {type: 'error', confirmButtonText: `Delete ${n}`, confirmButtonClass: 'el-button--danger'}).then(() => {
                this.loading = true;
                ApiService.delete('/buildium/all-bills/batch', {data: {delete_all_not_uploaded: true}}).then(r => {
                    this.detail = null;
                    this.$message.success(`Deleted ${(r && r.deleted_count) || n} unpublished bills`);
                    this.fetchList();
                }).catch(reason => {
                    this.$message.error(reason.message);
                }).finally(() => {
                    this.loading = false;
                });
            }).catch(() => {
            });
        },
        syncFromBuildium() {
            const loading = this.$loading({lock: true, text: 'Synchronizing with Buildium…', background: 'rgba(0, 0, 0, 0.5)'});
            ApiService.get('/buildium/all-bills/sync').then(() => {
                this.$message.success('Synchronized with Buildium');
                this.fetchList();
            }).catch(reason => {
                this.$message.error(reason.message);
            }).finally(() => {
                loading.close();
            });
        },
        startPolling(url, what) {
            this.publishProgress = 0;
            this.publishResults = null;
            clearInterval(this.publishPollingInterval);
            this.publishPollingInterval = setInterval(() => {
                ApiService.get(url).then(response => {
                    if (!response) return;
                    if (this.publishProgress < response.percentage) {
                        this.publishProgress = Math.min(this.publishProgress + 2, response.percentage);
                    }
                    this.publishResults = {total_success: response.success || 0, total_failed: response.failed || 0};
                    if (response.is_all_completed) {
                        clearInterval(this.publishPollingInterval);
                        this.publishProgress = 100;
                        this.$message.success(`${what} finished: ${response.success} done, ${response.failed} failed`);
                        this.fetchList();
                    }
                }).catch(() => {
                    clearInterval(this.publishPollingInterval);
                    this.$message.error('Could not read the progress');
                });
            }, 1000);
        },
    },
    mounted() {
        this.fetchList();
    },
    beforeDestroy() {
        clearInterval(this.publishPollingInterval);
        clearTimeout(this.searchTimer);
    },
}
</script>

<style scoped>
.bills-progress {
    padding: 12px 14px;
    margin-bottom: 12px;
}

.bills-progress-head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
    font-size: 14px;
}

.amount-negative {
    color: #B42318;
}

.bills-desc {
    margin: 0;
    white-space: pre-wrap;
    font-size: 14px;
}
</style>
